import { admin_dateDisplayFormatFull } from '@gik/l10n';
import moment from 'moment-timezone';
import numeral from 'numeral';
import React from 'react';
import DateTimeUtils, { dateFormat, dateTimeFormat, timeFormat } from './DateTimeUtils';

// TODO: the currency sign should be dynamic based on user settings.
const currencySign = '$';
// TODO: the number format should be dynamic based on user settings.
const numberFormat = '0,0';
// // TODO: the decimal number format should be dynamic based on user settings.
const numberFormatDecimal = '0,0.00';

const defaultTimeZone = 'America/Los_Angeles';

export interface FormatCurrencyReactOptions {
  /**
   * Render the signal in a <sup> tag
   */
  sup?: boolean;

  decimals?: boolean;
}

export interface FormatCurrencyOptions {
  decimals?: boolean;
}

/**
 * Format a number or string to be displayed as a currency.
 */
export function formatCurrencyReact(
  value: string | number,
  options: FormatCurrencyReactOptions = { decimals: true }
): React.ReactNode {
  let currency = <span>{currencySign}</span>;
  if (options.sup) currency = <sup>{currencySign}</sup>;
  return (
    <>
      {currency}
      {options.decimals ? formatNumberDecimal(value) : formatNumber(value)}
    </>
  );
}

/**
 * Format a number or string to be displayed as a currency.
 */
export function formatCurrency(value: string | number): string {
  const numericValue = typeof value === 'string' ? parseFloat(value) : value;
  const hasDecimals = numericValue % 1 !== 0;

  const amount = hasDecimals ? formatNumberDecimal(numericValue) : formatNumber(numericValue);

  return currencySign + amount;
}

/**
 * Format a number or string to be displayed as a number.
 */
export function formatNumber(value: string | number): string {
  return numeral(value).format(numberFormat);
}

/**
 * Format a number or string to be displayed as a number.
 */
export function formatNumberDecimal(value: string | number): string {
  return numeral(value).format(numberFormatDecimal);
}

/**
 * Format a date string to display as a date.
 */
export function formatDate(value: string | number | Date): string {
  return moment(value, DateTimeUtils.DateFormat).format(dateFormat);
}
/**
 * Format a date string to display as a date with time.
 */
export function formatDateTime(value: string | number | Date): string {
  return moment(value, DateTimeUtils.DateFormat).format(dateTimeFormat);
}

/**
 * Format a date string from a UTC ISO8601 source (with Z or offset) to admin display format in the given timezone.
 * Used for Strapi/Wallet, WooCommerce, and WordPress (e.g., "2025-01-27T08:24:09.488Z", "2018-05-11T08:06:07Z").
 */
export function adminFormatDateTimeTZ_ISO(value: string | number | Date, timeZone = defaultTimeZone): string {
  return moment.utc(value).tz(timeZone).format(admin_dateDisplayFormatFull);
}

/**
 * Format a date string from .NET backend (e.g., "2025-04-15T04:26:57.52" or "2022-03-14T00:00:00") to admin display format in the given timezone.
 * These are typically local server time (often UTC, but not always with Z). We treat as UTC unless otherwise specified.
 */
export function adminFormatDateTimeTZ_DotNet(value: string | number | Date, timeZone = defaultTimeZone): string {
  // .NET often omits Z, but is UTC. Parse as if UTC.
  // If value is a string and does not end with Z, append Z to treat as UTC.
  let str = value;
  if (typeof value === 'string' && !/Z$/.test(value) && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
    str = value + 'Z';
  }
  return moment.utc(str).tz(timeZone).format(admin_dateDisplayFormatFull);
}

/**
 * Legacy alias for backwards compatibility. Use the correct function for your backend.
 */
export const adminFormatDateTimeTZ = adminFormatDateTimeTZ_ISO;
/**
 * Format a date string to display as a time.
 */
export function formatTime(value: string | number | Date): string {
  return moment(value, DateTimeUtils.DateFormat).format(timeFormat);
}

export function formatRelativeTime(value: string | number | Date): string {
  return moment(value).fromNow();
}
